import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import Search from './components/Search';
import Categories, { CategoryOption } from './components/Categories';
import data from './data.json';
import { OfferWallBrands, OfferWallCategories } from './types';
import Brands from './components/Brands';
import { getBrandsToShow, getUniqueList } from './utils';

const Container = styled.div`
  padding-block: 20px;
`;

const Header = styled.header`
  top: 0;
  position: fixed;
  width: 100%;
  background-color: white;
  padding-top: 20px;
`;

const Main = styled.main`
  margin-top: 90px;
`;

type DocumentDir = 'rtl' | 'ltr';

interface Props {
  documentDir?: DocumentDir;
}
const OfferWall: React.FC<Props> = ({ documentDir }) => {
  const [searchKey, setSearchKey] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryOption>({
    value: 'all',
    name: 'All',
  });
  const [categories, setCategories] = useState<OfferWallCategories[]>([]);
  const allBrands = data.categories.flatMap((category) => category.brands);
  const dir = (documentDir ?? document.documentElement.dir) as DocumentDir;

  useEffect(() => {
    initPageData();
  }, []);

  useEffect(() => {
    initPageDir();
  }, [document?.dir, dir]);

  const initPageDir = () => {
    if (dir) {
      document.dir = dir;
    }
  };

  const initPageData = () => {
    if (!data.categories.find((category) => category.id === 'all')) {
      data.categories.unshift({
        name: 'All',
        id: 'all',
        arabicName: 'الكل',
        brands: allBrands,
      });
    }
    setCategories(data.categories);
  };

  const options = categories.map((item) => ({
    value: item?.id,
    name: item?.name,
    arabicName: item.arabicName,
  }));

  const brands = getBrandsToShow(
    categories,
    selectedCategory,
    allBrands,
    searchKey
  );

  const uniqueBrands = getUniqueList<OfferWallBrands>(brands).sort(
    (brand1, brand2) => brand1.name.localeCompare(brand2.name)
  );
  const uniqueCategoryOptions = getUniqueList<CategoryOption>(options);

  const onSearch = useCallback((text: string) => {
    setSelectedCategory({
      value: 'all',
      name: 'All',
      arabicName: 'الكل',
    });
    setSearchKey(text);
  }, []);

  const onCloseSearch = useCallback(() => {
    setSearchKey('');
  }, []);

  return (
    <Container>
      <Header>
        <Search
          placeholder={dir === 'rtl' ? 'بحث' : 'Search'}
          text={searchKey}
          onSearch={onSearch}
          onClose={onCloseSearch}
        />
        <Categories
          options={uniqueCategoryOptions}
          selectedOption={selectedCategory}
          setSelectedOption={(option) => setSelectedCategory(option)}
          getName={(option) =>
            dir === 'rtl' ? option?.arabicName : option?.name
          }
        />
      </Header>

      <Main>
        <Brands brands={uniqueBrands} />
      </Main>
    </Container>
  );
};

export default OfferWall;
